
import { Box, Button, CircularProgress, IconButton, InputAdornment, TextField, Typography, styled } from '@mui/material';
import { FC, useCallback, useContext, useState } from 'react';
import { Link as RouteLink, useParams } from 'react-router-dom';

import { grey } from '@mui/material/colors';
import AuthContext from '../../contexts/auth/AuthContext';
import Hr from '../common/HorizontalRule';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/pro-regular-svg-icons';


const LoginContainer = styled(Box)(() => ({
    backgroundColor: 'white',
    padding: '15px',
    borderRadius: '4px'
}));

const StyledTextField = styled(TextField)(() => ({
    marginBottom: '15px'
}));

const StyledLoginButton = styled(Button)(({ theme }) => ({
    maxWidth: '100px',
    minWidth: '100px',
    minHeight: '40px',
    maxHeight: '40px',
    backgroundColor: theme.palette.primary.main,

    "&:hover": {
        backgroundColor: theme.palette.primary.main,
        boxShadow: '0px 0px 6px 1px rgba(75,90,35,0.5)'
    },

    "&:active": {
        backgroundColor: theme.palette.secondary.main
    }
}));

const LoginForm: FC = () => {

    const { authenticate, closeLogin, loginMessage } = useContext(AuthContext);

    const [loading, setLoading] = useState<boolean>(false);

    const [userName, setUserName] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [showPassword, setShowPassword] = useState<boolean>(false);

    const handleSubmit = useCallback(async () => {
        setLoading(true);

        const loggedIn = await authenticate(userName, password);

        if (loggedIn) {
            // TODO: Give ability to navigate to default location
            //       on login.

            closeLogin();
        }

        setLoading(false);

    }, [userName, password]);

    const { species: speciesName } = useParams();

    return (
        <LoginContainer onClick={(e) => { e.stopPropagation() }}>
            <Box>

                <Typography variant='h6' color={grey[700]}>LOG IN</Typography>
                <Hr color={grey[200]}/>
                <Typography color='#333' margin='15px 0'>Please enter your username and password.</Typography>

                <Typography color='#333'>Username</Typography>
                <StyledTextField 
                    id='login-username'
                    autoComplete='username'
                    fullWidth 
                    variant='outlined' 
                    size='small'
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                />

                <Typography color='#333'>Password</Typography>
                <StyledTextField 
                    id='login-password'
                    autoComplete='current-password'
                    fullWidth
                    variant='outlined' 
                    size='small' 
                    type={!showPassword ? 'password' : 'text'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    disableRipple
                                    sx={{ width: '24px', height: '24px' }}
                                    onClick={() => setShowPassword(prev => !prev)}
                                >
                                    {
                                        showPassword ?
                                            <FontAwesomeIcon icon={faEye} style={{ fontSize: '1.2rem' }} /> :
                                            <FontAwesomeIcon icon={faEyeSlash} style={{ fontSize: '1.2rem' }} />
                                    }
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
                { loginMessage && (
                    <Typography color='red'>{loginMessage}</Typography>
                )}
                <Box component={RouteLink} to={`/${speciesName}/forgotpassword`} onClick={closeLogin}>Forgot password?</Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        margin: '15px 0'
                    }}
                >
                    <StyledLoginButton
                        onClick={handleSubmit}
                    >
                        {
                            loading ? 
                                <CircularProgress sx={{ color: 'white', maxHeight: '25px', maxWidth: '25px' }} /> :
                                <Typography color={grey[100]} fontWeight='600'>Log In</Typography>

                        }
                    </StyledLoginButton>

                    <StyledLoginButton
                        onClick={() => closeLogin()}
                    >
                        <Typography color={grey[100]} fontWeight='600'>Cancel</Typography>
                    </StyledLoginButton>
                </Box>

                <Hr color={grey[200]}/>
                
                <Typography color='#333' margin='15px 0'>Don't have an account?</Typography> 
                <Box component={RouteLink} to={`/${speciesName}/register`} onClick={closeLogin}>Create a new account</Box>
            </Box>
        </LoginContainer>
    )
}

export default LoginForm